import { useEffect, useRef, useState } from 'react';
import { Palette, AudioLines, Image as ImageIcon, Type, Video, AlertTriangle } from 'lucide-react';
import { FILMSTRIP, customFontFamily, formatDuration } from '@vidmakr/shared';
import { ensureCustomFont } from '@/lib/fonts';
import { mediaRetryDelay, useMediaUrls } from '@/lib/media';
import { Progress, Spinner, cx } from './ui';

export interface AssetLike {
  id: string;
  name: string;
  kind: string;
  status: string;
  posterKey: string;
  filmstripKey: string;
  filmstripTiles: number;
  filmstripIntervalUs: number;
  width: number;
  height: number;
  durationUs: number;
  error: string;
  /** For fonts: the file, so a replaced font is loaded again. */
  storageKey?: string;
}

export function filmstripTileHeight(w: number, h: number) {
  return Math.max(2, Math.round((FILMSTRIP.tileWidth * h) / Math.max(1, w) / 2) * 2);
}

/**
 * A lazy-loaded media image that retries with backoff when the load fails
 * (bucket GETs fail now and then when many images load at once). Each retry
 * mounts a fresh element, which makes the browser request it again.
 */
export function MediaImg({ src, className }: { src: string; className?: string }) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [src]);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  const onError = () => {
    const wait = mediaRetryDelay(attempt);
    if (wait === null) return setFailed(true);
    timer.current = setTimeout(() => setAttempt((a) => a + 1), wait);
  };
  if (failed) return null;
  return <img key={attempt} src={src} alt="" className={className} loading="lazy" draggable={false} onError={onError} />;
}

/** Poster with hover-scrubbing through the filmstrip. `compact` (table rows) leaves out the badges and status text. */
export function AssetThumb({ asset, progress, className, compact }: { asset: AssetLike; progress?: number; className?: string; compact?: boolean }) {
  const isFont = asset.kind === 'font';
  const isLut = asset.kind === 'lut';
  const urls = useMediaUrls(asset.status === 'ready' && !isFont && !isLut ? asset.id : null, asset.posterKey + asset.filmstripKey);
  const [fontLoaded, setFontLoaded] = useState(false);
  useEffect(() => {
    if (!isFont || asset.status === 'error') return;
    let alive = true;
    void ensureCustomFont(asset.id, asset.storageKey).then((ok) => alive && setFontLoaded(ok));
    return () => {
      alive = false;
    };
  }, [isFont, asset.id, asset.status, asset.storageKey]);
  const [hover, setHover] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const tiles = asset.filmstripTiles;
  const th = filmstripTileHeight(asset.width, asset.height);
  const rows = Math.ceil(tiles / FILMSTRIP.columns);
  const onMove = (e: React.MouseEvent) => {
    if (!tiles || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setHover(Math.min(tiles - 1, Math.max(0, Math.floor(((e.clientX - r.left) / r.width) * tiles))));
  };
  const Icon = asset.kind === 'audio' ? AudioLines : asset.kind === 'image' ? ImageIcon : isFont ? Type : isLut ? Palette : Video;
  return (
    <div
      ref={ref}
      className={cx('relative aspect-video overflow-hidden bg-panel-3', className)}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
    >
      {hover !== null && urls.filmstrip ? (
        <div
          className="h-full w-full bg-no-repeat"
          style={{
            backgroundImage: `url(${urls.filmstrip})`,
            backgroundSize: `${FILMSTRIP.columns * 100}% ${rows * 100}%`,
            backgroundPosition: `${((hover % FILMSTRIP.columns) / Math.max(1, FILMSTRIP.columns - 1)) * 100}% ${rows > 1 ? (Math.floor(hover / FILMSTRIP.columns) / (rows - 1)) * 100 : 0}%`,
            aspectRatio: `${FILMSTRIP.tileWidth}/${th}`,
          }}
        />
      ) : fontLoaded ? (
        <div className="flex h-full w-full items-center justify-center text-4xl text-fg" style={{ fontFamily: `"${customFontFamily(asset.id)}"` }}>
          Aa
        </div>
      ) : urls.poster ? (
        <MediaImg src={urls.poster} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-faint">
          {asset.status === 'error' ? <AlertTriangle size={compact ? 14 : 22} className="text-danger" /> : <Icon size={compact ? 14 : 24} />}
        </div>
      )}
      {(asset.status === 'uploading' || asset.status === 'processing') && compact ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/55 text-white">
          <Spinner size={12} />
        </div>
      ) : asset.status === 'uploading' || asset.status === 'processing' ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/55 px-4 text-xs text-white">
          <Spinner size={16} />
          {asset.status === 'uploading' ? 'Uploading' : 'Processing'}
          {progress !== undefined ? <Progress value={progress} className="max-w-[120px]" /> : null}
        </div>
      ) : null}
      {compact ? null : asset.status === 'cold' ? (
        <div className="absolute right-1.5 top-1.5 rounded bg-black/70 px-1 py-0.5 text-[10px] text-white" title="Not used for a while: the editing copy is rebuilt when a project using it opens" data-asset-cold>
          Archived
        </div>
      ) : null}
      {asset.durationUs > 0 && !compact ? (
        <div className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1 py-0.5 font-mono text-[10px] text-white">
          {formatDuration(asset.durationUs)}
        </div>
      ) : null}
      {compact ? null : (
        <div className="absolute left-1.5 top-1.5 rounded bg-black/60 p-0.5 text-white">
          <Icon size={12} />
        </div>
      )}
    </div>
  );
}
